import { useMemo, useState } from 'react';

import { Button } from 'ui';

import {
  useTutorWalletTransactions,
  type WalletTransaction,
} from 'hooks/useTutorWallet';
import { formatSignedRub } from 'utils/formatMoneyRu';

import { getWalletTransactionIcon, WALLET_PAGE_SIZE, WALLET_PERIOD_OPTIONS, type WalletPeriod } from './walletUi';

import classes from './WalletTransactionHistory.module.scss';

function formatDate(value?: string | null): string {
  if (!value) return ' - ';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return ' - ';
  return date.toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

type WalletTransactionHistoryProps = {
  embeddedClassPrefix?: string;
};

export const WalletTransactionHistory = (_props: WalletTransactionHistoryProps) => {
  const [period, setPeriod] = useState<WalletPeriod>('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(0);

  const offset = page * WALLET_PAGE_SIZE;
  const { data, isLoading, isFetching } = useTutorWalletTransactions({
    limit: WALLET_PAGE_SIZE,
    offset,
    period: dateFrom || dateTo ? undefined : period,
    dateFrom: dateFrom || undefined,
    dateTo: dateTo || undefined,
  });

  const totalPages = useMemo(() => {
    const total = data?.total ?? 0;
    return Math.max(1, Math.ceil(total / WALLET_PAGE_SIZE));
  }, [data?.total]);

  const applyPeriod = (next: WalletPeriod) => {
    setPeriod(next);
    setDateFrom('');
    setDateTo('');
    setPage(0);
  };

  const renderRow = (tx: WalletTransaction) => {
    const positive = !tx.amount_rub.startsWith('-');
    const { Icon, tone } = getWalletTransactionIcon(tx.type);

    return (
      <li key={tx.id} className={classes.item}>
        <span className={`${classes.icon} ${classes[`icon--${tone}`]}`} aria-hidden>
          <Icon size={18} strokeWidth={2} />
        </span>
        <div className={classes.body}>
          <strong>{tx.type_label}</strong>
          {tx.description && <span>{tx.description}</span>}
          {tx.receipt_number && <span className={classes.receipt}>Квитанция {tx.receipt_number}</span>}
        </div>
        <div className={classes.meta}>
          <span className={`${classes.amount} ${classes[`amount--${tone}`]}`}>
            {formatSignedRub(tx.amount_rub, positive)}
          </span>
          <time>{formatDate(tx.created_at)}</time>
        </div>
      </li>
    );
  };

  return (
    <section className={classes.section}>
      <div className={classes.header}>
        <h3>История операций</h3>
        {isFetching && !isLoading && <span className={classes.loading}>Обновляем...</span>}
      </div>

      <div className={classes.filters}>
        <div className={classes.periods}>
          {WALLET_PERIOD_OPTIONS.map((option) => (
            <button
              key={option.value}
              type='button'
              className={`${classes.chip} ${period === option.value && !dateFrom && !dateTo ? classes['chip--active'] : ''}`}
              onClick={() => applyPeriod(option.value)}
            >
              {option.label}
            </button>
          ))}
        </div>
        <div className={classes.dates}>
          <label>
            <span>С</span>
            <input
              type='date'
              value={dateFrom}
              onChange={(event) => {
                setDateFrom(event.target.value);
                setPage(0);
              }}
            />
          </label>
          <label>
            <span>По</span>
            <input
              type='date'
              value={dateTo}
              onChange={(event) => {
                setDateTo(event.target.value);
                setPage(0);
              }}
            />
          </label>
        </div>
      </div>

      {isLoading ? (
        <p className={classes.empty}>Загрузка операций...</p>
      ) : !data?.items.length ? (
        <p className={classes.empty}>Операций за выбранный период нет.</p>
      ) : (
        <ul className={classes.list}>{data.items.map(renderRow)}</ul>
      )}

      {data && data.total > WALLET_PAGE_SIZE && (
        <div className={classes.pagination}>
          <Button
            variant='outlined'
            color='primary'
            size='sm'
            shape='brick'
            disabled={page <= 0}
            onClick={() => setPage((value) => Math.max(0, value - 1))}
          >
            Назад
          </Button>
          <span>
            {page + 1} / {totalPages}
          </span>
          <Button
            variant='outlined'
            color='primary'
            size='sm'
            shape='brick'
            disabled={page + 1 >= totalPages}
            onClick={() => setPage((value) => value + 1)}
          >
            Далее
          </Button>
        </div>
      )}
    </section>
  );
};
